import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { Users, Building2, FileText, TrendingUp, AlertCircle, Receipt, FolderOpen, UserCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminAnalytics from '@/components/admin/AdminAnalytics';
import AdminTeamManagement from '@/components/admin/AdminTeamManagement';
import CompanySettings from '@/components/admin/CompanySettings';
import ReactivationRequestsManagement from '@/components/admin/ReactivationRequestsManagement';
import AdminSettings from '@/components/admin/AdminSettings';
import CompaniesManagement from '@/components/admin/CompaniesManagement';
import CompanyReactivationRequests from '@/components/admin/CompanyReactivationRequests';
import UsersManagement from '@/components/admin/UsersManagement';
import ResponsiveTabsList from '@/components/shared/ResponsiveTabsList';

interface CompanyAnalyticsData {
  total_users: number;
  total_customers: number;
  total_estimates: number;
  total_invoices: number;
  total_projects: number;
  pending_reactivation_requests: number;
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('analytics');
  const [analyticsData, setAnalyticsData] = useState<Partial<CompanyAnalyticsData>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingCompanyRequestsCount, setPendingCompanyRequestsCount] = useState(0);
  
  const { user, isAdmin, isSuperuser, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const showUsersTab = !isSuperuser || !!profile?.company_id;
  const showCompanyTab = !isSuperuser || !!profile?.company_id;
  const showReactivationTab = !isSuperuser || !!profile?.company_id;

  // Read active tab from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!isAdmin && !isSuperuser) {
      navigate('/dashboard');
      return;
    }

    const fetchCompanyAnalytics = async () => {
      setIsLoading(true);
      try {
        if (!profile?.company_id) {
          setIsLoading(false);
          return;
        }

        // Fetch company-specific analytics
        const { data: companyAnalytics, error: analyticsError } = await supabase
          .rpc('get_company_analytics', { p_company_id: profile.company_id });

        if (analyticsError) {
          console.error('Error fetching company analytics:', analyticsError);
        } else if (companyAnalytics && companyAnalytics.length > 0) {
          const data = companyAnalytics[0];
          setAnalyticsData({
            total_users: Number(data.total_users) || 0,
            total_customers: Number(data.total_customers) || 0,
            total_estimates: Number(data.total_estimates) || 0,
            total_invoices: Number(data.total_invoices) || 0,
            total_projects: Number(data.total_projects) || 0,
            pending_reactivation_requests: Number(data.pending_reactivation_requests) || 0
          });
          setPendingRequestsCount(Number(data.pending_reactivation_requests) || 0);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyAnalytics();
  }, [isAdmin, isSuperuser, navigate, profile?.company_id]);

  // Fetch count of pending company-level reactivation requests (superuser only)
  useEffect(() => {
    if (!isSuperuser) return;
    let cancelled = false;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('company_suspension_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (!cancelled && !error) {
        setPendingCompanyRequestsCount(count || 0);
      }
    };
    fetchCount();
    return () => { cancelled = true; };
  }, [isSuperuser, activeTab]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          {isSuperuser ? 'Platform Admin' : 'Company Admin Dashboard'}
        </h1>
        <p className="text-muted-foreground">
          {isSuperuser
            ? 'Manage companies and users across the Doyence platform'
            : 'Manage company users and settings'}
        </p>
      </div>

      {/* Analytics Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5 mb-8">
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setActiveTab('users')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Company Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : analyticsData.total_users?.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate('/customers')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Company Customers</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : analyticsData.total_customers?.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate('/estimates')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Estimates</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : analyticsData.total_estimates?.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate('/invoices')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : analyticsData.total_invoices?.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate('/projects')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? '...' : analyticsData.total_projects?.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <ResponsiveTabsList className="mb-6">
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          {showUsersTab && <TabsTrigger value="users">Users</TabsTrigger>}
          {showCompanyTab && <TabsTrigger value="company">Company</TabsTrigger>}
          {showReactivationTab && (
            <TabsTrigger value="reactivation-requests" className="relative">
              Reactivation Requests
              {pendingRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingRequestsCount}
                </span>
              )}
            </TabsTrigger>
          )}
          {isSuperuser && <TabsTrigger value="companies">Companies</TabsTrigger>}
          {isSuperuser && (
            <TabsTrigger value="company-requests" className="relative">
              Company Requests
              {pendingCompanyRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingCompanyRequestsCount}
                </span>
              )}
            </TabsTrigger>
          )}
          {isSuperuser && <TabsTrigger value="platform-users">Platform Users</TabsTrigger>}
          {isSuperuser && <TabsTrigger value="settings">Settings</TabsTrigger>}
        </ResponsiveTabsList>

        <TabsContent value="analytics">
          <AdminAnalytics analyticsData={analyticsData} />
        </TabsContent>

        {showUsersTab && (
          <TabsContent value="users">
            <AdminTeamManagement isSuperuser={isSuperuser} />
          </TabsContent>
        )}

        {showCompanyTab && (
          <TabsContent value="company">
            <CompanySettings />
          </TabsContent>
        )}

        {showReactivationTab && (
          <TabsContent value="reactivation-requests">
            <ReactivationRequestsManagement companyId={profile?.company_id || ''} />
          </TabsContent>
        )}

        {isSuperuser && (
          <TabsContent value="companies">
            <CompaniesManagement />
          </TabsContent>
        )}

        {isSuperuser && (
          <TabsContent value="company-requests">
            <CompanyReactivationRequests />
          </TabsContent>
        )}

        {isSuperuser && (
          <TabsContent value="platform-users">
            <UsersManagement isSuperuser={isSuperuser} />
          </TabsContent>
        )}

        {isSuperuser && (
          <TabsContent value="settings">
            <AdminSettings isSuperuser={isSuperuser} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}